import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import Layout from "../../components/Layout";
import {
  ListEmptyState,
  ListTableLoading,
} from "../../components/ListSearchFilters";
import { useAuth } from "../../hooks/auth";
import { useToast } from "../../hooks/toast";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";
import {
  LIST_SEARCH_DEBOUNCE_MS,
  buildPagedListPath,
  parseOfferStatusFilter,
  shouldSyncSearchToFirstPage,
} from "../../utils/listFilterQuery";
import { OffersListFilters } from "./OffersListFilters";
import { OffersListTable, OfferListItem } from "./OffersListTable";
import { ListPagination } from "../../components/ListPagination";

interface IParams {
  page: string;
}

function buildOffersPath(page: number, search: string, status: string): string {
  return buildPagedListPath("/offers", page, { search, status });
}

export default function Offers() {
  const history = useHistory();
  const location = useLocation();
  const { params } = useRouteMatch<IParams>();
  const { dateToExpires, access_token, user } = useAuth();
  const { addToast } = useToast();

  const currentPage = Number(params.page) || 1;
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const statusFilter = parseOfferStatusFilter(query.get("status"));
  const urlSearch = (query.get("search") ?? "").trim();

  const [searchTerm, setSearchTerm] = useState(urlSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearch);
  const [offers, setOffers] = useState<OfferListItem[]>([]);
  const [lastPage, setLastPage] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, LIST_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    if (!shouldSyncSearchToFirstPage(debouncedSearch, urlSearch)) return;

    const nextUrl = buildOffersPath(1, debouncedSearch, statusFilter);
    const currentUrl = `${history.location.pathname}${history.location.search}`;
    if (currentUrl !== nextUrl) history.push(nextUrl);
  }, [debouncedSearch, statusFilter, urlSearch, history]);

  useEffect(() => {
    if (!user) {
      history.push("/");
      return;
    }

    if (shouldSyncSearchToFirstPage(debouncedSearch, urlSearch)) return;

    async function loadOffers() {
      setLoading(true);
      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

        const response = await api.get("/api/adm/offers", {
          params: {
            page: currentPage,
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            ...(statusFilter ? { status: statusFilter } : {}),
          },
        });

        setOffers(response.data.data.data ?? []);
        setLastPage(Number(response.data.data.last_page) || 0);
      } catch (err: any) {
        if (err.response?.data?.message) {
          addToast({
            title: err.response.data.message,
            type: "error",
          });
        }
      } finally {
        setLoading(false);
      }
    }

    loadOffers();
  }, [
    user,
    access_token,
    dateToExpires,
    history,
    currentPage,
    debouncedSearch,
    urlSearch,
    statusFilter,
    addToast,
  ]);

  return (
    <Layout>
      <div className="mx-auto flex w-full max-w-7xl flex-1 flex-col px-2 py-4 font-[inherit] md:px-3 md:py-5">
        <div className="mb-4">
          <h3 className="text-2xl font-bold text-slate-800">Propostas</h3>
          <p className="mt-1 text-sm text-slate-500">
            Consulte, pesquise e acompanhe as propostas de locação.
          </p>
        </div>

        <OffersListFilters
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          onSearchChange={setSearchTerm}
          onStatusChange={(value) => {
            history.push(buildOffersPath(1, debouncedSearch, value));
          }}
        />

        {loading ? (
          <ListTableLoading />
        ) : offers.length === 0 ? (
          <ListEmptyState message="Nenhuma proposta encontrada para esta busca." />
        ) : (
          <OffersListTable offers={offers} />
        )}

        <ListPagination
          currentPage={currentPage}
          totalPages={lastPage}
          onPageChange={(page) =>
            history.push(buildOffersPath(page, debouncedSearch, statusFilter))
          }
        />
      </div>
    </Layout>
  );
}
